/**
 * The model as the file reads: every element, nested, editable in place.
 *
 * This replaces the Files screen. Files answered "what did BEST import?", which
 * is a question about the plumbing; the question people actually arrive with is
 * "where in the model is this thing, and what is next to it?" - so the tree is
 * the whole model, in containment order, and the .sysml export moves here as one
 * action among several rather than being the point of a screen.
 *
 * Three things it deliberately shows rather than hides:
 *
 *   - Elements BEST does not project. They are marked "not projected" and offer
 *     no edit. Omitting them would make the tree quietly disagree with the file,
 *     which is worse than admitting there is no form for them.
 *   - The shared library, when asked for. Off by default because it is not this
 *     program's to edit - a program declaring INTO the library was a real defect
 *     - and every edit on a library row is refused with that as the reason.
 *   - Every action, on every row, disabled with its reason. §5.1.
 *
 * A package re-opened in several files - every enclosing package of a split
 * program (issue #27) - is shown ONCE, with its files listed (issue #53).
 *
 * Something new is stated through one New dialog, on a package row or from the
 * toolbar: each kind's own wizard, or SysML text. Drag-to-move is in the design
 * and is not here: it needs a route that does not exist (see §7).
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import FormControlLabel from '@mui/material/FormControlLabel'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { ApiError, api } from '../api'
import type { ElementRow, SourceFileRow } from '../schema'
import EmptyState from '../components/common/EmptyState'
import AddElementDialog from '../components/tree/AddElementDialog'
import NewElementDialog from '../components/tree/NewElementDialog'
import DeleteElementDialog from '../components/tree/DeleteElementDialog'
import ElementSourceDialog from '../components/tree/ElementSourceDialog'
import ModelTreeBranch from '../components/tree/ModelTreeBranch'
import RenameElementDialog from '../components/tree/RenameElementDialog'
import {
  allKeys,
  countNodes,
  destinationPackage,
  packageKeys,
  prune,
  toTree,
} from '../components/tree/tree-model'
import type { TreeAction, TreeNode } from '../components/tree/tree-model'
import { useCanWrite, useSchema } from '../state/AppData'
import { useToasts } from '../state/Toasts'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import { readSearch, writeSearch } from '../components/entity/rowFilter'
import { useRememberedFilters } from '../components/entity/rememberedFilters'

/** The endpoint this screen is nothing without, named where it is missing. */
const MISSING =
  'The tree needs GET /api/elements, which is not deployed yet. It answers every element in the ' +
  'model as flat rows carrying a depth.'

function shortPath(path: string): string {
  return path.split(/[\\/]/).pop() ?? path
}

export default function ModelTree() {
  const schema = useSchema()
  const write = useCanWrite()
  const toasts = useToasts()
  const navigate = useNavigate()

  const [rows, setRows] = useState<ElementRow[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  /**
   * The search, in the URL under `search` as on every other list (issue #86).
   *
   * It was component state, so it was gone the moment anything else was opened
   * and the box was empty on the way back. In the URL it is a link, and
   * remembered so that the sidebar and the breadcrumbs put it back. Replace, not
   * push: typing must not fill up the back button.
   */
  const [params, setParams] = useSearchParams()
  const search = readSearch(params)
  const setSearch = (next: string) =>
    setParams((now) => writeSearch(now, next), { replace: true })
  useRememberedFilters()
  const [includeLibrary, setIncludeLibrary] = useState(false)
  const [files, setFiles] = useState<SourceFileRow[]>([])
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null)
  /** Bumped after a write, to re-read the tree the write changed. */
  const [reloads, setReloads] = useState(0)
  const [acting, setActing] = useState<{ action: TreeAction; node: TreeNode } | null>(null)
  /** The toolbar's New, which has no row and so no package yet. */
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    let live = true
    setRows(null)
    setError(null)
    api
      .elements(includeLibrary)
      .then((items) => {
        if (live) setRows(items)
      })
      .catch((failure: ApiError) => {
        if (live) setError(failure)
      })
    return () => {
      live = false
    }
  }, [includeLibrary, reloads])

  // Separate, and allowed to fail quietly: whole-model export is a useful thing
  // on this screen, not the reason for it.
  useEffect(() => {
    api
      .files()
      .then(setFiles)
      .catch(() => setFiles([]))
  }, [])

  const tree = useMemo(() => (rows ? toTree(rows) : []), [rows])
  const shown = useMemo(() => prune(tree, search), [tree, search])
  const expanded = useMemo(
    () => (search ? allKeys(shown) : packageKeys(shown)),
    [shown, search],
  )

  /** After a write: re-read the tree, and say so unless the dialog already did. */
  const finished = (message?: string) => {
    setActing(null)
    setCreating(false)
    if (message) toasts.succeeded(message)
    setReloads((count) => count + 1)
  }

  const act = (action: TreeAction, node: TreeNode) => {
    if (action === 'reveal') {
      navigate(`/${node.row.plural}/${encodeURIComponent(node.row.id)}`)
      return
    }
    setActing({ action, node })
  }

  if (error) {
    // A 404 is the back end not being deployed yet, which is a state rather
    // than a fault: say which route is missing instead of showing a red alert
    // about a URL nobody typed. §4.
    if (error.status === 404) {
      return (
        <Stack spacing={2}>
          <Header />
          <Paper sx={{ p: 2 }}>
            <EmptyState title="The model tree is not available yet" description={MISSING} />
          </Paper>
        </Stack>
      )
    }
    return (
      <Stack spacing={2}>
        <Header />
        <Alert severity="error">{error.message}</Alert>
      </Stack>
    )
  }

  if (!rows) {
    return (
      <Stack spacing={2}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      <Header />

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, minWidth: 260 }}>
          <EntityFilterBar
            plural="elements"
            value={search}
            onChange={setSearch}
            hint="ancestors kept"
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeLibrary}
              onChange={(event) => setIncludeLibrary(event.target.checked)}
            />
          }
          label="Show the shared library"
        />
        <Tooltip title={write.allowed ? '' : write.reason} describeChild>
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!write.allowed}
              onClick={() => setCreating(true)}
            >
              New
            </Button>
          </span>
        </Tooltip>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          disabled={files.length === 0}
          onClick={(event) => setExportAnchor(event.currentTarget)}
        >
          Export .sysml
        </Button>
        <Menu
          open={Boolean(exportAnchor)}
          anchorEl={exportAnchor}
          onClose={() => setExportAnchor(null)}
        >
          {files.map((file) => (
            <MenuItem
              key={file.id}
              component="a"
              href={`/api/files/${file.id}/export`}
              download={shortPath(file.path)}
              onClick={() => setExportAnchor(null)}
            >
              {shortPath(file.path)}
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      <Paper sx={{ p: 2 }}>
        {shown.length === 0 ? (
          <EmptyState
            title={search ? `Nothing matches “${search}”` : 'There is nothing in the model'}
            description={
              search
                ? undefined
                : 'Use “Import from GitLab…” in the program menu to populate it.'
            }
          />
        ) : (
          <SimpleTreeView
            // Re-mounted when the filter changes so the expansion follows the
            // result rather than whatever happened to be open before it.
            key={search ? `filter:${search}` : 'tree'}
            defaultExpandedItems={expanded}
            sx={{ '& .MuiTreeItem-content': { py: 0.35 } }}
          >
            {shown.map((node) => (
              <ModelTreeBranch key={node.key} node={node} write={write} onAction={act} />
            ))}
          </SimpleTreeView>
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary">
        {countNodes(shown)} of {countNodes(tree)} element(s) shown
        {includeLibrary ? ', including the shared library' : ''}.
      </Typography>

      {acting?.action === 'source' && (
        <ElementSourceDialog row={acting.node.row} onClose={() => setActing(null)} />
      )}
      {acting?.action === 'rename' && (
        <RenameElementDialog
          row={acting.node.row}
          onClose={() => setActing(null)}
          onRenamed={finished}
        />
      )}
      {acting?.action === 'delete' && (
        <DeleteElementDialog
          row={acting.node.row}
          onClose={() => setActing(null)}
          onDeleted={finished}
        />
      )}
      {acting?.action === 'new' && (
        <NewElementDialog
          schema={schema}
          pkg={destinationPackage(acting.node, 'child')}
          onClose={() => setActing(null)}
          onCreated={finished}
        />
      )}
      {creating && (
        <NewElementDialog
          schema={schema}
          onClose={() => setCreating(false)}
          onCreated={finished}
        />
      )}
      {acting?.action === 'duplicate' && (
        <AddElementDialog
          schema={schema}
          pkg={destinationPackage(acting.node, 'sibling')}
          source={acting.node.row}
          onClose={() => setActing(null)}
          onCreated={finished}
        />
      )}
    </Stack>
  )
}

function Header() {
  return (
    <Box>
      <Typography variant="h5">Model tree</Typography>
      <Typography variant="body2" color="text.secondary">
        Every element the model holds, in the order the file reads. Showing an element&apos;s
        source regenerates it from stored structure rather than serving the text that was
        imported.
      </Typography>
    </Box>
  )
}
